import React, { useState, useEffect } from 'react';
import { Table, Segmented, Checkbox, Button, Space, message, Popconfirm, Tag, Row, Col } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckCircleOutlined, ClockCircleOutlined, CloseCircleOutlined, MinusCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { appointmentApi } from '../api/appointment';
import { Appointment, AppointmentStatus } from '../api/model';

const statusOptions = [
  { label: '待确认', value: AppointmentStatus.Pending },
  { label: '已确认', value: AppointmentStatus.Confirmed },
  { label: '拒绝', value: AppointmentStatus.Rejected },
  { label: '取消', value: AppointmentStatus.Cancelled },
];

const Appointments: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [appointmentType, setAppointmentType] = useState<'future' | 'history'>('future');
  const [selectedStatuses, setSelectedStatuses] = useState<AppointmentStatus[]>([AppointmentStatus.Pending, AppointmentStatus.Confirmed]);
  // 添加选中的行状态
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // 分页状态
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // 固定每页20条
  const [total, setTotal] = useState(0);

  // 获取预约数据
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      // 设置startTime为今日0点
      const todayStart = dayjs().startOf('day').toISOString();
      
      // 根据预约类型设置排序方向
      const ascending = appointmentType === 'future';
      
      const params = {
        start_time: todayStart,
        status_list: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        ascending,
        page: currentPage,
        page_size: pageSize
      };
      
      const response = await appointmentApi.listAppointmentsPage(params);
      if (response.success) {
        setAppointments(response.data.data || []);
        setTotal(response.data.total || 0);
      } else {
        message.error('获取预约数据失败: ' + response.message);
      }
    } catch (error) {
      console.error('获取预约数据失败:', error);
      message.error('获取预约数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理类型切换
  const handleTypeChange = (value: string | number) => {
    setAppointmentType(value as 'future' | 'history');
    // 切换类型时重置到第一页
    setCurrentPage(1);
  };

  // 处理状态选择变化
  const handleStatusChange = (checkedValues: any[]) => {
    setSelectedStatuses(checkedValues);
    // 切换状态时重置到第一页
    setCurrentPage(1);
  };

  // 处理分页变化
  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
  };

  // 确认预约
  const confirmAppointment = async (id: string) => {
    try {
      const response = await appointmentApi.confirmAppointment(id);
      if (response.success) {
        message.success('预约已确认');
        // 重新获取数据
        fetchAppointments();
      } else {
        message.error('确认预约失败: ' + response.message);
      }
    } catch (error) {
      console.error('确认预约失败:', error);
      message.error('确认预约失败');
    }
  };

  // 拒绝预约
  const rejectAppointment = async (id: string) => {
    try {
      const response = await appointmentApi.rejectAppointment(id);
      if (response.success) {
        message.success('预约已拒绝');
        // 重新获取数据
        fetchAppointments();
      } else {
        message.error('拒绝预约失败: ' + response.message);
      }
    } catch (error) {
      console.error('拒绝预约失败:', error);
      message.error('拒绝预约失败');
    }
  };

  // 批量确认预约
  const confirmAppointments = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一个预约');
      return;
    }
    
    try {
      // 通过发送多个请求来实现批量确认
      const promises = selectedRowKeys.map(id => appointmentApi.confirmAppointment(id as string));
      const responses = await Promise.all(promises);
      
      // 检查所有请求是否都成功
      const allSuccess = responses.every(response => response.success);
      
      if (allSuccess) {
        message.success(`成功确认 ${selectedRowKeys.length} 个预约`);
        // 清空选择
        setSelectedRowKeys([]);
        // 重新获取数据
        fetchAppointments();
      } else {
        // 如果有任何请求失败，显示错误信息
        const failedCount = responses.filter(response => !response.success).length;
        message.error(`批量确认预约失败: ${failedCount} 个预约确认失败`);
      }
    } catch (error) {
      console.error('批量确认预约失败:', error);
      message.error('批量确认预约失败');
    }
  };

  // 批量拒绝预约
  const rejectAppointments = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一个预约');
      return;
    }
    
    try {
      // 通过发送多个请求来实现批量拒绝
      const promises = selectedRowKeys.map(id => appointmentApi.rejectAppointment(id as string));
      const responses = await Promise.all(promises);
      
      // 检查所有请求是否都成功
      const allSuccess = responses.every(response => response.success);
      
      if (allSuccess) {
        message.success(`成功拒绝 ${selectedRowKeys.length} 个预约`);
        // 清空选择
        setSelectedRowKeys([]);
        // 重新获取数据
        fetchAppointments();
      } else {
        // 如果有任何请求失败，显示错误信息
        const failedCount = responses.filter(response => !response.success).length;
        message.error(`批量拒绝预约失败: ${failedCount} 个预约拒绝失败`);
      }
    } catch (error) {
      console.error('批量拒绝预约失败:', error);
      message.error('批量拒绝预约失败');
    }
  };

  // 状态标签映射
  const getStatusLabel = (status: AppointmentStatus) => {
    switch (status) {
      case AppointmentStatus.Pending: return '待确认';
      case AppointmentStatus.Confirmed: return '已确认';
      case AppointmentStatus.Cancelled: return '取消';
      case AppointmentStatus.Rejected: return '拒绝';
      default: return '';
    }
  };

  // 表格行选择配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  useEffect(() => {
    fetchAppointments();
  }, [currentPage, appointmentType, selectedStatuses]);

  const columns: ColumnsType<Appointment> = [
    {
      title: '患者姓名',
      dataIndex: ['patient', 'user', 'name'],
      key: 'patient_name',
    },
    {
      title: '性别',
      dataIndex: ['patient', 'gender'],
      key: 'patient_gender',
      render: (gender: string) => (gender === 'male' ? '男' : gender === 'female' ? '女' : '未知'),
    },
    {
      title: '年龄',
      key: 'patient_age',
      render: (_, record) => {
        if (record.patient.birth_date) {
          const birthDate = dayjs(record.patient.birth_date);
          const now = dayjs();
          return now.diff(birthDate, 'year');
        }
        return '未知';
      },
    },
    {
      title: '预约时间',
      key: 'appointment_time',
      render: (_, record) => (
        <>
          <div>{dayjs(record.start_time).format('YYYY-MM-DD')}</div>
          <div>{dayjs(record.start_time).format('HH:mm')} - {dayjs(record.end_time).format('HH:mm')}</div>
        </>
      ),
    },
    {
      title: '地点',
      dataIndex: ['schedule_location', 'name'],
      key: 'schedule_location',
      render: (_, record) => record.schedule_location?.name || '-',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: AppointmentStatus) => {
        switch (status) {
          case AppointmentStatus.Confirmed:
            return <Tag color="processing" icon={<CheckCircleOutlined />}>{getStatusLabel(status)}</Tag>;
          case AppointmentStatus.Pending:
            return <Tag color="warning" icon={<ClockCircleOutlined />}>{getStatusLabel(status)}</Tag>;
          case AppointmentStatus.Rejected:
            return <Tag color="default" icon={<CloseCircleOutlined />}>{getStatusLabel(status)}</Tag>;
          case AppointmentStatus.Cancelled:
            return <Tag color="default" icon={<MinusCircleOutlined />}>{getStatusLabel(status)}</Tag>;
          default:
            return <Tag>--</Tag>;
        }
      },
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          {/* 只有在未来预约列表中且状态为待确认时，才显示确认/拒绝按钮 */}
          {appointmentType === 'future' && record.status === AppointmentStatus.Pending && (
            <>
              <Popconfirm
                title="确认预约"
                description="确定要接受这个预约吗？"
                onConfirm={() => confirmAppointment(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="primary" size="small">
                  确认
                </Button>
              </Popconfirm>
              <Popconfirm
                title="拒绝预约"
                description="确定要拒绝这个预约吗？"
                onConfirm={() => rejectAppointment(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button size="small">
                  拒绝
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col flex="1 1 1px">
          <Space size="large">
            <Segmented
              options={[{ label: '未来预约', value: 'future' }, { label: '历史预约', value: 'history' }]} 
              value={appointmentType}
              onChange={handleTypeChange}
            />
            <Checkbox.Group
              options={statusOptions}
              value={selectedStatuses}
              onChange={handleStatusChange}
            />
          </Space>
        </Col>
        {/* 批量操作按钮 */}
        {selectedRowKeys.length > 0 && (
          <Col flex="0 0 auto">
            <Space size="large">
              <Popconfirm
                title="批量确认预约"
                description={`确定要确认选中的 ${selectedRowKeys.length} 个预约吗？`}
                onConfirm={confirmAppointments}
                okText="确定"
                cancelText="取消"
              >
                <Button type="primary">
                  批量接受
                </Button>
              </Popconfirm>
              <Popconfirm
                title="批量拒绝预约"
                description={`确定要拒绝选中的 ${selectedRowKeys.length} 个预约吗？`}
                onConfirm={rejectAppointments}
                okText="确定"
                cancelText="取消"
              >
                <Button>
                  批量拒绝
                </Button>
              </Popconfirm>
            </Space>
          </Col>
        )}
      </Row>
      <Table 
        columns={columns} 
        dataSource={appointments} 
        loading={loading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize,
          total,
          onChange: handlePaginationChange,
          showSizeChanger: false, // 不允许改变每页大小
          showTotal: (total) => `共 ${total} 条记录`
        }}
        rowSelection={rowSelection}  // 添加行选择功能
      />
    </>
  );
};

export default Appointments;